import React, { useState, useEffect } from 'react';
import { Save, X, Plus, Edit, Trash2, Target, Calendar } from 'lucide-react';
import { useGoals } from '../hooks/useGoals';
import { DOMAINS, REVIEW_FREQUENCIES, GOAL_STATUSES } from '../utils/dataModels';
import Tooltip from './Tooltip';

const GoalEditor = ({ goals, students, goalId, onNavigate, announce }) => {
  const { addGoal, updateGoal, deleteGoal } = useGoals();
  const [showForm, setShowForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [selectedStudentFilter, setSelectedStudentFilter] = useState('all');
  const [selectedDomainFilter, setSelectedDomainFilter] = useState('all');
  const [formData, setFormData] = useState({
    studentId: '',
    domain: 'Academic',
    description: '',
    measurementMethod: '',
    targetCriteria: '',
    reviewFrequency: 'Monthly',
    status: 'Not Started'
  });

  // Filter goals based on selected filters
  const filteredGoals = goals.filter(goal => {
    const studentMatch = selectedStudentFilter === 'all' || goal.studentId === selectedStudentFilter;
    const domainMatch = selectedDomainFilter === 'all' || goal.domain === selectedDomainFilter;
    return studentMatch && domainMatch;
  });

  // Group goals by student for better organization
  const goalsByStudent = filteredGoals.reduce((acc, goal) => {
    const student = students.find(s => s.id === goal.studentId);
    if (student) {
      const key = `${student.firstName} ${student.lastName}`;
      if (!acc[key]) {
        acc[key] = { student, goals: [] };
      }
      acc[key].goals.push(goal);
    }
    return acc;
  }, {});

  useEffect(() => {
    if (goalId) {
      const goal = goals.find(g => g.id === goalId);
      if (goal) {
        setEditingGoal(goal);
        setFormData(goal);
        setShowForm(true);
      }
    }
  }, [goalId, goals]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.studentId || !formData.description.trim()) {
      announce('Please fill in all required fields', 'assertive');
      return;
    }

    if (editingGoal) {
      const success = updateGoal(editingGoal.id, formData);
      if (success) {
        announce('Goal updated successfully');
        resetForm();
      }
    } else {
      const newGoal = addGoal(formData);
      if (newGoal) {
        announce('Goal created successfully');
        resetForm();
      }
    }
  };

  const resetForm = () => {
    setFormData({
      studentId: '',
      domain: 'Academic',
      description: '',
      measurementMethod: '',
      targetCriteria: '',
      reviewFrequency: 'Monthly',
      status: 'Not Started'
    });
    setEditingGoal(null);
    setShowForm(false);
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setFormData(goal);
    setShowForm(true);
    announce(`Editing goal: ${goal.description.substring(0, 50)}...`);
  };

  const handleDeleteGoal = (goalId, description) => {
    if (window.confirm(`Are you sure you want to delete this goal: "${description.substring(0, 50)}..."?`)) {
      const success = deleteGoal(goalId);
      if (success) {
        announce('Goal deleted successfully');
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Not Started': 'bg-gray-100 text-gray-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Mastered': 'bg-green-100 text-green-800',
      'Discontinued': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const GoalForm = () => (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {editingGoal ? 'Edit Goal' : 'Create New Goal'}
        </h2>
        <button
          onClick={resetForm}
          className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Close form"
        >
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="student" className="block text-sm font-medium text-gray-900 mb-2">
              Student <span className="text-red-500">*</span>
            </label>
            <select
              id="student"
              value={formData.studentId}
              onChange={(e) => handleInputChange('studentId', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            >
              <option value="">Select a student...</option>
              {students.map(student => (
                <option key={student.id} value={student.id}>
                  {student.firstName} {student.lastName} (Grade {student.grade})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="domain" className="block text-sm font-medium text-gray-900 mb-2">
              Domain
            </label>
            <select
              id="domain"
              value={formData.domain}
              onChange={(e) => handleInputChange('domain', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {DOMAINS.map(domain => (
                <option key={domain} value={domain}>{domain}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-900 mb-2">
            Goal Description <span className="text-red-500">*</span>
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            rows={3}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Enter a clear, measurable goal description..."
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="measurement" className="block text-sm font-medium text-gray-900 mb-2">
              Measurement Method
            </label>
            <input
              type="text"
              id="measurement"
              value={formData.measurementMethod}
              onChange={(e) => handleInputChange('measurementMethod', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="How will progress be measured?"
            />
          </div>

          <div>
            <label htmlFor="target" className="block text-sm font-medium text-gray-900 mb-2">
              Target Criteria
            </label>
            <input
              type="text"
              id="target"
              value={formData.targetCriteria}
              onChange={(e) => handleInputChange('targetCriteria', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="e.g., 80% accuracy over 3 trials"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="frequency" className="block text-sm font-medium text-gray-900 mb-2">
              Review Frequency
            </label>
            <select
              id="frequency"
              value={formData.reviewFrequency}
              onChange={(e) => handleInputChange('reviewFrequency', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {REVIEW_FREQUENCIES.map(freq => (
                <option key={freq} value={freq}>{freq}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-900 mb-2">
              Status
            </label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {GOAL_STATUSES.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={resetForm}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <Save size={16} className="mr-2" />
            {editingGoal ? 'Update Goal' : 'Create Goal'}
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Goals</h1>
          <p className="text-gray-900 mt-1">
            Create and manage IEP goals ({filteredGoals.length} goals)
          </p>
        </div>
        
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          <Plus size={16} className="mr-2" />
          Add Goal
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label htmlFor="student-filter" className="block text-sm font-medium text-gray-900 mb-1">
              Filter by Student
            </label>
            <select
              id="student-filter"
              value={selectedStudentFilter}
              onChange={(e) => setSelectedStudentFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Students</option>
              {students.map(student => (
                <option key={student.id} value={student.id}>
                  {student.firstName} {student.lastName}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex-1">
            <label htmlFor="domain-filter" className="block text-sm font-medium text-gray-900 mb-1">
              Filter by Domain
            </label>
            <select
              id="domain-filter"
              value={selectedDomainFilter}
              onChange={(e) => setSelectedDomainFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Domains</option>
              {DOMAINS.map(domain => (
                <option key={domain} value={domain}>{domain}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Goal Form */}
      {showForm && <GoalForm />}

      {/* Goals List */}
      <div className="space-y-6">
        {Object.keys(goalsByStudent).length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <Target size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No goals found</h3>
            <p className="text-gray-500 mb-4">
              {selectedStudentFilter !== 'all' || selectedDomainFilter !== 'all'
                ? 'Try adjusting your filters or create a new goal.'
                : 'Create your first IEP goal to get started.'}
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary bg-primary/10 hover:bg-primary/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              <Plus size={16} className="mr-2" />
              Create Goal
            </button>
          </div>
        ) : (
          Object.entries(goalsByStudent).map(([studentName, { student, goals }]) => (
            <div key={student.id} className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Calendar size={20} className="mr-2 text-primary" />
                    {studentName} - {student.grade} Grade
                  </h3>
                  <span className="text-sm text-gray-900">
                    {goals.length} goal{goals.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
              
              <div className="divide-y divide-gray-200">
                {goals.map(goal => (
                  <div key={goal.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {goal.domain}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(goal.status)}`}>
                            {goal.status}
                          </span>
                        </div>
                        
                        <p className="text-gray-900 font-medium mb-2">{goal.description}</p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-900">
                          {goal.measurementMethod && (
                            <div>
                              <span className="font-medium">Measurement:</span> {goal.measurementMethod}
                            </div>
                          )}
                          {goal.targetCriteria && (
                            <div>
                              <span className="font-medium">Target:</span> {goal.targetCriteria}
                            </div>
                          )}
                          <div>
                            <span className="font-medium">Review:</span> {goal.reviewFrequency}
                          </div>
                          <div>
                            <span className="font-medium">Progress Entries:</span> {goal.progressData.length}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-4">
                        <Tooltip content="Add Progress">
                          <button
                            onClick={() => onNavigate('progress', { goalId: goal.id })}
                            className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                            aria-label={`Add progress for ${goal.description.substring(0, 30)}...`}
                          >
                            <Target size={16} />
                          </button>
                        </Tooltip>
                        <Tooltip content="Edit Goal">
                          <button
                            onClick={() => handleEditGoal(goal)}
                            className="p-2 text-gray-900 hover:text-gray-700 hover:bg-gray-50 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
                            aria-label={`Edit ${goal.description.substring(0, 30)}...`}
                          >
                            <Edit size={16} />
                          </button>
                        </Tooltip>
                        <Tooltip content="Delete Goal">
                          <button
                            onClick={() => handleDeleteGoal(goal.id, goal.description)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                            aria-label={`Delete ${goal.description.substring(0, 30)}...`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default GoalEditor;